import { validateOffset } from "@artis/sdk";

import type { Value } from "@package/scratchpad-utils";
import { type FnContext, FormulaError, type FunctionDef } from "./interpreter";

const KEY_SEPARATOR = "|";

export function curveKey(product: string, field: string, offset: string | number): string {
  return [product, field, String(offset)].join(KEY_SEPARATOR);
}

export function parseCurveKey(key: string): { product: string; field: string; offset: string } {
  const [product = "", field = "", offset = ""] = key.split(KEY_SEPARATOR);
  return { product, field, offset };
}

export function toApiOffset(offset: string): string | number {
  return /^\d+$/.test(offset) ? Number(offset) : offset;
}

type InsertableCurve = { kind?: "curve"; id: string; field: string; offsets?: readonly (string | number)[] };
type InsertablePeriod = { kind: "period"; offsets: readonly (string | number)[] };

/** An offset as it should read in a formula: numbers bare, everything else canonically spelled. */
function offsetLiteral(offset: string | number): string | null {
  if (typeof offset === "number") return validateOffset(offset).error ? null : String(offset);
  const { normalized } = validateOffset(offset);
  return normalized ? `"${normalized}"` : null;
}

function refCall(ref: InsertableCurve | InsertablePeriod): string | null {
  const offsets = (ref.offsets ?? []).map(offsetLiteral).filter((o): o is string => o !== null);
  if (offsets.length === 0) return null;
  const args = ref.kind === "period" ? offsets : [`"${ref.id}"`, `"${ref.field}"`, ...offsets];
  return `${ref.kind === "period" ? "PERIOD" : "CURVE"}(${args.join(",")})`;
}

/**
 * Outside an argument list a period column is dropped whenever a curve column is picked, and
 * several curve columns become one `SUM(...)`, so a horizontal pick into an empty cell reads as a
 * total rather than a chain of `+`.
 */
export function selectionToFormula(
  refs: readonly (InsertableCurve | InsertablePeriod)[],
  separator: "+" | ",",
): string | null {
  const calls = refs.map(refCall).filter((c): c is string => c !== null);
  if (separator === "+") {
    const curves = calls.filter((c) => c.startsWith("CURVE("));
    if (curves.length > 1) return `SUM(${curves.join(",")})`;
    if (curves.length === 1) return curves[0]!;
  }
  return calls.length > 0 ? calls.join(separator) : null;
}

/** Rejects an offset the SDK grammar cannot read, so a typo reads as `#VALUE!` rather than a missing value. */
function checkedOffset(offset: Value): string {
  const text = String(offset);
  const { error } = validateOffset(text);
  if (error) throw new FormulaError("#VALUE!", error);
  return text;
}

export const LOADING_CODE = "#LOADING";

function resolveCurveValue(fnCtx: FnContext, key: string): Value {
  const value = fnCtx.external(key);
  if (value === undefined) throw new FormulaError(LOADING_CODE);
  if (value === null) throw new FormulaError("#N/A");
  return value;
}

export const CURVE: FunctionDef = {
  fn: (args, fnCtx) => {
    if (args.length !== 3) throw new FormulaError("#VALUE!");
    const [product, field, offset] = args.map((a) => a.eval());
    return resolveCurveValue(fnCtx, curveKey(String(product), String(field), checkedOffset(offset)));
  },
  values: (args, fnCtx) => {
    if (args.length < 3) throw new FormulaError("#VALUE!");
    const product = String(args[0]!.eval());
    const field = String(args[1]!.eval());
    const keys = args.slice(2).map((arg) => curveKey(product, field, checkedOffset(arg.eval())));
    return keys.map((key) => resolveCurveValue(fnCtx, key));
  },
  params: ["product", "field", "offset1", "[offset2]", "…"],
  description: "Live curve value(s) for a product field at the given offsets",
};

const LOADING_GRACE_MS = 5_000;

export type CurveCache = {
  resolve: (key: string) => Value | null | undefined;
  push: (key: string, value: number | null) => void;
  begin: (key: string) => void;
  end: (key: string) => void;
  subscribe: (cb: (key: string) => void) => () => void;
};

export function createCurveCache(graceMs = LOADING_GRACE_MS): CurveCache {
  const values = new Map<string, number | null>();
  const deadlines = new Map<string, number>();
  const subscribers = new Set<(key: string) => void>();
  const notify = (key: string) => {
    for (const cb of subscribers) cb(key);
  };
  const arm = (key: string) => {
    deadlines.set(key, Date.now() + graceMs);
    setTimeout(() => notify(key), graceMs);
  };
  return {
    // A never-begun key arms its own grace on first resolve, so it reads #LOADING, not #N/A.
    resolve: (key) => {
      const value = values.get(key);
      if (typeof value === "number") return value;
      const deadline = deadlines.get(key);
      if (deadline === undefined && value === undefined) {
        arm(key);
        return undefined;
      }
      if (deadline !== undefined && Date.now() < deadline) return undefined;
      return value ?? null;
    },
    push: (key, value) => {
      values.set(key, value);
      notify(key);
    },
    begin: (key) => {
      arm(key);
      notify(key);
    },
    end: (key) => {
      deadlines.delete(key);
    },
    subscribe: (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
  };
}
